<template>
  <div class="live-quiz-list-page">
    <header ref="listHeader" class="live-list-header" :class="{'is-floating': headerFloating}">
      <button type="button" class="live-list-back" :aria-label="$t('Back')" @click.stop.prevent="back">
        <img src="static/images/livequiz/back.png" alt="">
      </button>
      <span class="live-list-info" aria-hidden="true">
        <img src="static/images/livequiz/info.png" alt="">
      </span>
    </header>

    <main class="live-list-main">
      <section class="live-event-poster" :aria-label="$t('LiveQuizChallenge')">
        <img src="static/images/livequiz/quize_logo.png" :alt="$t('LiveQuizChallenge')">
      </section>

      <nav class="live-list-tabs" :aria-label="$t('LiveQuizSchedule')">
        <button v-for="tab in tabs" :key="tab.value" :class="{active: activeTab === tab.value}" @click="changeTab(tab.value)">
          {{ tab.label }}
        </button>
      </nav>

      <section v-if="rounds.length" class="live-round-list" :class="{'is-history-list': activeTab === 'history'}">
        <article v-for="(round, index) in rounds" :key="round.id" class="live-round-card" :class="{'is-ended': isEnded(round), 'is-history': activeTab === 'history'}">
          <div class="round-status-ribbon">{{ roundStatus(round) }}</div>
          <div class="round-card-heading">
            <img src="static/images/livequiz/malt_r.png" alt="">
            <div v-if="activeTab !== 'history'" class="round-heading-content">
              <strong class="round-title">
                <span class="round-title-word">{{ $t('LiveQuizRound') }}</span>
                <span class="round-title-number">{{ index + 1 }}</span>
              </strong>
              <span class="round-title-time">{{ formatRoundTime(round.start_at) }}</span>
            </div>
            <strong v-else class="round-history-time">{{ formatRoundTime(round.start_at) }}</strong>
            <img src="static/images/livequiz/malt_l.png" alt="">
          </div>
          <div class="round-card-reward">
            <span>{{ roundMetricLabel(round) }}</span>
            <div class="round-points">
              <strong>{{ roundRewardTotal(round) }}</strong>
              <small>{{ roundRewardUnit(round) }}</small>
            </div>
          </div>
          <button class="round-card-action" :class="{'is-ended': isEnded(round), 'is-countdown': !isEnded(round) && isReservedBeforeStart(round)}" :disabled="!canUseAction(round)" @click="handleRoundAction(round)">
            {{ roundAction(round) }}
          </button>
        </article>
      </section>

      <div v-if="!rounds.length && !loading" class="live-list-empty">{{ $t('LiveQuizNoRounds') }}</div>
      <button v-if="hasMore" class="live-list-more" :disabled="loadingMore" @click="loadMore">
        {{ loadingMore ? $t('LiveQuizLoading') : $t('LiveQuizLoadMore') }}
      </button>

      <section v-if="showWinnerInfo" class="live-winner-info">
        <div class="winner-info-heading">
          <img src="static/images/livequiz/malt_r.png" alt="">
          <strong>{{ $t('LiveQuizWinnerInfo') }}</strong>
          <img src="static/images/livequiz/malt_l.png" alt="">
        </div>
        <div class="winner-lane">
          <div class="winner-track">
            <div v-for="(winner, index) in winnerTrack" :key="'winner-a-' + index" class="winner-chip">
              <img class="winner-avatar" :src="winner.avatar || 'static/images/logo.png'" alt="">
              <span>{{ $t('LiveQuizWinnerReceived', {name: winner.nickname, points: winner.points}) }}</span>
            </div>
          </div>
        </div>
        <div class="winner-lane winner-lane-reverse">
          <div class="winner-track">
            <div v-for="(winner, index) in winnerTrack" :key="'winner-b-' + index" class="winner-chip">
              <img class="winner-avatar" :src="winner.avatar || 'static/images/logo.png'" alt="">
              <span>{{ $t('LiveQuizWinnerReceived', {name: winner.nickname, points: winner.points}) }}</span>
            </div>
          </div>
        </div>
      </section>

    </main>
  </div>
</template>

<script>
export default {
  data () {
    return {
      activeTab: ['history', 'today', 'tomorrow'].indexOf(this.$route.query.tab) !== -1 ? this.$route.query.tab : 'today',
      rounds: [],
      winnerInfo: [],
      page: 1,
      hasMore: false,
      loading: false,
      loadingMore: false,
      now: Date.now(),
      clockTimer: null,
      refreshTimer: null,
      latestRequestId: 0,
      headerFloating: false
    }
  },
  computed: {
    tabs () {
      return [
        {value: 'history', label: this.$t('LiveQuizHistory')},
        {value: 'today', label: this.$t('LiveQuizToday')},
        {value: 'tomorrow', label: this.$t('LiveQuizTomorrow')}
      ]
    },
    showWinnerInfo () {
      return this.activeTab === 'today' && this.winnerInfo.length > 0
    },
    // 复制一份用于无缝滚动；接口最多只取 20 位机器人，不会增加请求量。
    winnerTrack () {
      return this.winnerInfo.concat(this.winnerInfo)
    }
  },
  created () {
    this.loadRounds()
    this.clockTimer = setInterval(() => { this.now = Date.now() }, 250)
    this.refreshTimer = setInterval(() => { this.loadRounds() }, 15000)
  },
  mounted () {
    this.$nextTick(() => {
      this.updateHeaderFloating()
      window.addEventListener('scroll', this.updateHeaderFloating, {passive: true})
    })
  },
  beforeDestroy () {
    clearInterval(this.clockTimer)
    clearInterval(this.refreshTimer)
    window.removeEventListener('scroll', this.updateHeaderFloating)
  },
  methods: {
    updateHeaderFloating () {
      const header = this.$refs.listHeader
      this.headerFloating = Boolean(header && header.getBoundingClientRect().top <= 14)
    },
    changeTab (tab) {
      if (this.activeTab === tab) return
      this.activeTab = tab
      this.loadRounds()
    },
    loadRounds (append = false) {
      const page = append ? this.page + 1 : 1
      const period = this.activeTab
      const requestId = ++this.latestRequestId
      if (append) this.loadingMore = true
      else this.loading = true
      this.$api.get('/api/live_quiz/rounds', {period: period, page: page, limit: 20}, r => {
        // Tab 切换或下一次轮询已发起时，丢弃旧响应，避免旧数据短暂覆盖当前按钮状态。
        if (requestId !== this.latestRequestId || period !== this.activeTab) return
        this.loading = false
        this.loadingMore = false
        this.$parent.returnCode(r, () => {
          const data = r.data || {}
          const list = data.list || []
          const receivedAt = Date.now()
          const previousById = {}
          if (!append) {
            this.rounds.forEach(round => { previousById[round.id] = round })
          }
          const updatedList = list.map(round => {
            const previous = previousById[round.id]
            if (previous) {
              Object.assign(previous, round)
              // 后端每次返回的是新的剩余秒数，基准时间也必须同步重置，避免重复扣减。
              previous._startCountdownStartedAt = receivedAt
              return previous
            }
            round._startCountdownStartedAt = receivedAt
            return round
          })
          this.rounds = append ? this.rounds.concat(updatedList) : updatedList
          this.page = Number(data.page || page)
          this.hasMore = Boolean(data.has_more)
          if (!append) {
            this.winnerInfo = Array.isArray(data.winner_info) ? data.winner_info : []
          }
        })
      })
    },
    loadMore () {
      if (!this.hasMore || this.loadingMore) return
      this.loadRounds(true)
    },
    handleRoundAction (round) {
      if (this.isEnded(round)) {
        this.$router.push({path: '/liveQuizResult', query: {round_id: round.id, tab: this.activeTab}})
        return
      }
      if (this.hasMissedLocalStart(round)) {
        this.loadRounds()
        return
      }
      if (round.user_state === 'can_reserve') {
        this.reserveRound(round)
        return
      }
      if (this.isReservedBeforeStart(round)) {
        this.enterLobbyWhenAvailable(round)
        return
      }
      if (['lobby', 'live', 'spectating', 'passed'].indexOf(round.user_state) !== -1) {
        this.$router.push({path: '/liveQuiz', query: {round_id: round.id}})
      }
    },
    reserveRound (round) {
      this.$api.post('/api/live_quiz/reserve', {round_id: round.id}, r => {
        this.$parent.returnCode(r, () => {
          this.$utils.alertText(this.$t('LiveQuizReserved'), 80)
          this.loadRounds()
        })
      })
    },
    enterLobbyWhenAvailable (round) {
      // 进入等待区的边界以接口的缅甸服务器时间为准，不使用前端本机时间判断。
      this.$api.get('/api/live_quiz/detail', {round_id: round.id}, r => {
        this.$parent.returnCode(r, () => {
          const latest = r.data || {}
          Object.assign(round, latest)
          round._startCountdownStartedAt = Date.now()
          if (this.isLobbyOpen(round)) {
            this.$router.push({path: '/liveQuiz', query: {round_id: round.id}})
            return
          }
          const message = this.activeTab === 'tomorrow'
            ? this.$t('LiveQuizTomorrowReservedHint')
            : this.$t('LiveQuizLobbyAvailableInTenMinutes')
          this.$utils.alertText(message, 80)
        })
      })
    },
    roundStatus (round) {
      if (this.isEnded(round)) return this.$t('LiveQuizStatusEnded')
      if (this.hasMissedLocalStart(round)) return this.$t('LiveQuizStatusStarted')
      if (round.user_state === 'can_reserve') return this.$t('LiveQuizStatusBooking')
      if (round.user_state === 'reserved_countdown') return this.$t('LiveQuizStatusReserved')
      if (round.user_state === 'not_open') return this.$t('LiveQuizStatusPreview')
      if (round.user_state === 'lobby' || round.user_state === 'live') return this.$t('LiveQuizStatusStarting')
      if (round.user_state === 'missed') return this.$t('LiveQuizStatusStarted')
      if (round.user_state === 'reservation_closed') return this.$t('LiveQuizStatusClosed')
      return this.$t('LiveQuizStatusStarting')
    },
    roundAction (round) {
      if (this.isEnded(round)) return this.$t('LiveQuizStatusEnded')
      if (this.hasMissedLocalStart(round)) return this.$t('LiveQuizUnableToJoin')
      if (round.user_state === 'can_reserve') return this.$t('LiveQuizReserve')
      if (this.activeTab === 'tomorrow' && this.isReservedBeforeStart(round)) return this.$t('LiveQuizNotStarted')
      if (this.isReservedBeforeStart(round)) return this.formatCountdown(round)
      if (this.isLobbyOpen(round)) return this.$t('LiveQuizEnterLobby')
      if (round.user_state === 'not_open') return this.$t('LiveQuizStatusPreview')
      if (round.user_state === 'lobby') return this.$t('LiveQuizEnterLobby')
      if (round.user_state === 'missed') return this.$t('LiveQuizUnableToJoin')
      if (round.user_state === 'reservation_closed') return this.$t('LiveQuizStatusClosed')
      return this.$t('LiveQuizEnter')
    },
    roundMetricLabel (round) {
      if (this.isEnded(round)) {
        return this.$t('LiveQuizPassedPlayers', {count: round.pass_count || 0})
      }
      return this.$t('LiveQuizMaxRewardHint', {count: round.question_count || 0})
    },
    roundRewardTotal (round) {
      return round.reward_total === undefined || round.reward_total === null ? (round.max_points || 0) : round.reward_total
    },
    roundRewardUnit (round) {
      return round.reward_type === 'loyalty' ? this.$t('LiveQuizLoyalty') : this.$t('LiveQuizPts')
    },
    isReservedBeforeStart (round) {
      if (this.isEnded(round)) return false
      return Boolean(round.reserved) && this.remainingStartSeconds(round) > 0
    },
    isLobbyOpen (round) {
      return round.user_state === 'lobby'
    },
    hasMissedLocalStart (round) {
      return round.user_state === 'lobby' && this.remainingStartSeconds(round) <= 0 && !round.entry_eligible
    },
    canUseAction (round) {
      if (this.hasMissedLocalStart(round)) return false
      if (this.isEnded(round) || round.user_state === 'can_reserve' || this.isReservedBeforeStart(round)) return true
      return ['lobby', 'live', 'spectating', 'passed'].indexOf(round.user_state) !== -1
    },
    isEnded (round) {
      return round.status === 'ended' || round.user_state === 'ended'
    },
    remainingStartSeconds (round) {
      const elapsed = Math.floor((this.now - Number(round._startCountdownStartedAt || this.now)) / 1000)
      return Math.max(0, Number(round.start_countdown_seconds || 0) - elapsed)
    },
    formatCountdown (round) {
      const total = this.remainingStartSeconds(round)
      const hours = String(Math.floor(total / 3600)).padStart(2, '0')
      const minutes = String(Math.floor((total % 3600) / 60)).padStart(2, '0')
      const secs = String(total % 60).padStart(2, '0')
      return hours + ':' + minutes + ':' + secs
    },
    formatRoundTime (time) {
      if (!time) return ''
      const parts = String(time).split(' ')
      const date = (parts[0] || '').replace(/-/g, '.')
      const rawClock = (parts[1] || '').slice(0, 5)
      const rawHour = Number(rawClock.slice(0, 2))
      // 场次显示沿用后端的 24 小时制；AM/PM 仅作为时段标记。
      const clock = rawClock + (rawHour >= 12 ? ' PM' : ' AM')
      return this.activeTab === 'history' ? date.slice(5) + ' · ' + clock : clock
    },
    back () {
      this.$router.replace({name: 'index'})
    }
  }
}
</script>

<style scoped>
.live-quiz-list-page { position:relative; min-height:100vh; box-sizing:border-box; isolation:isolate; padding:46px 28px 96px; color:#fff; background:#06021d url('../../static/images/livequiz/main_bg.jpg') center top/100% auto no-repeat; font-family:Arial,sans-serif; }
.live-quiz-list-page::before { position:absolute; z-index:0; inset:0; content:''; background:rgba(7,0,26,.3); pointer-events:none; }
.live-list-header,.live-list-main { position:relative; z-index:1; width:100%; max-width:650px; margin:0 auto; }
.live-list-header { position:sticky; top:14px; z-index:30; display:flex; align-items:center; justify-content:space-between; pointer-events:none; }.live-list-header.is-floating::before { position:absolute; z-index:-1; top:-14px; bottom:-4px; left:50%; width:100vw; content:''; background:rgba(8,2,38,.86); box-shadow:0 9px 22px rgba(0,0,0,.32); backdrop-filter:blur(8px); transform:translateX(-50%); }.live-list-header > * { pointer-events:auto; }
.live-list-back,.live-list-info { display:flex; width:72px; height:72px; align-items:center; justify-content:center; padding:0; border:0; background:transparent; }
.live-list-back { position:relative; z-index:2; pointer-events:auto; }
.live-list-back img,.live-list-info img { display:block; width:100%; height:100%; object-fit:contain; }
.live-event-poster { display:flex; height:430px; align-items:center; justify-content:center; margin:-18px auto -4px; }
.live-event-poster img { display:block; width:120%;  object-fit:contain; filter:drop-shadow(0 14px 24px rgba(0,0,0,.5)); }
.live-list-tabs { display:flex; align-items:center; justify-content:center; gap:5px; margin:0 -10px 30px; }
.live-list-tabs button { position:relative; display:flex; min-width:0; height:68px; flex:1; align-items:center; justify-content:center; padding:0 7px 7px; border:0; color:#fff; background:transparent url('../../static/images/livequiz/type_normal.png') center/100% 100% no-repeat; font-size:24px; font-weight:800; line-height:1; text-shadow:0 3px 4px rgba(19,0,32,.72); white-space:nowrap; }
.live-list-tabs button.active { height:80px; padding-bottom:11px; background-image:url('../../static/images/livequiz/type_act.png'); color:#fff8ce; font-size:25px; }
.live-round-list { display:flex; flex-direction:column; gap:28px; }
.live-round-card { position:relative; display:flex; min-height:430px; box-sizing:border-box; align-items:center; flex-direction:column; overflow:hidden; padding:28px 30px 20px; background:transparent url('../../static/images/livequiz/match_bg.png') center/100% 100% no-repeat; filter:drop-shadow(0 16px 20px rgba(0,0,0,.36)); }
.live-round-card.is-history { min-height:410px; }
.round-status-ribbon { position:absolute; top:0; right:0; display:flex; width:150px; height:78px; align-items:center; justify-content:center; box-sizing:border-box; padding:0 10px 0 22px; color:#fff9e9; background:transparent url('../../static/images/livequiz/status.png') right top/100% 100% no-repeat; font-size:19px; font-style:italic; font-weight:800; line-height:1.1; text-align:center; text-shadow:0 3px 6px rgba(41,0,15,.72); }
.round-card-heading { display:flex; width:100%; min-height:70px; align-items:center; justify-content:flex-start; gap:10px; padding:0 144px 0 25px; box-sizing:border-box; color:#fff; text-align:left; }
.round-card-heading img { width:36px; max-height:62px; flex:none; object-fit:contain; }
.round-heading-content { display:flex; min-width:0; align-items:baseline; gap:9px; }
.round-title { display:flex; min-width:0; align-items:baseline; gap:7px; color:#fff; font-style:italic; font-weight:900; line-height:1; white-space:nowrap; text-shadow:0 4px 9px rgba(0,0,0,.5); }
.round-title-word { font-size:31px; }
.round-title-number { font-size:50px; letter-spacing:-2px; }
.round-title-time { margin-top:0; color:#fff7d4; font-size:25px; font-style:italic; font-weight:800; line-height:1; white-space:nowrap; text-shadow:0 3px 7px rgba(0,0,0,.55); }
.round-history-time { overflow:hidden; color:#fff; font-size:32px; font-style:italic; font-weight:900; line-height:1.15; text-overflow:ellipsis; white-space:nowrap; text-shadow:0 4px 9px rgba(0,0,0,.5); }
.round-card-reward { display:flex; width:100%; min-height:160px; align-items:center; justify-content:center; flex-direction:column; margin:6px 0; background:transparent url('../../static/images/livequiz/match_pts_bg.png') center/100% 100% no-repeat; text-align:center; }
.is-history .round-card-reward { min-height:150px; margin-top:4px; }
.round-card-reward > span { padding:0 24px; color:#fff; font-size:21px; font-weight:700; line-height:1.25; text-shadow:0 3px 5px rgba(28,0,19,.72); }
.round-points { display:flex; align-items:flex-end; justify-content:center; gap:0; margin-top:5px; }
.round-points strong { display:block; min-width:0; flex:0 0 auto; color:#fff36d; background:linear-gradient(180deg,#fffde6 0%,#fff56d 27%,#ffd24a 52%,#ff8a67 76%,#fa42d6 100%) no-repeat; background-size:100% 100%; -webkit-background-clip:text; background-clip:text; -webkit-text-fill-color:transparent; -webkit-text-stroke:1px rgba(255,247,183,.3); font-size:104px; font-style:normal; font-weight:900; line-height:.88; text-align:center; transform:skewX(-12deg); transform-origin:center bottom; }
.round-points small { position:relative; z-index:1; flex:none; margin-left:14px; padding-bottom:8px; color:#fff7bf; font-size:24px; font-weight:900; text-shadow:0 3px 5px rgba(0,0,0,.5); }
.round-card-action { width:70%; min-height:0; aspect-ratio:754 / 167; margin-top:25px; border:0; border-radius:0; color:#fff; background:transparent url('../../static/images/livequiz/btn_bg.png') center/100% 100% no-repeat; font-size:35px; font-weight:900; line-height:1; text-shadow:0 3px 5px rgba(81,0,42,.65); }
.round-card-action.is-countdown { color:#fff6c0; font-variant-numeric:tabular-nums; }
.round-card-action:disabled { color:#d6cce0; box-shadow:inset 0 1px 0 rgba(255,255,255,.14); cursor:not-allowed; filter:grayscale(1) brightness(.66); text-shadow:none; }
.live-winner-info { position:relative; min-height:255px; overflow:hidden; margin-top:30px; padding:34px 0 24px; border-radius:40px; background:transparent url('../../static/images/livequiz/winner_info_bg.png') center/100% 100% no-repeat; }
.winner-info-heading { display:flex; align-items:center; justify-content:flex-start; gap:10px; padding-left:58px; color:#fff; }
.winner-info-heading img { width:36px; max-height:62px; object-fit:contain; }
.winner-info-heading strong { font-size:32px; font-style:italic; font-weight:900; text-shadow:0 4px 8px rgba(0,0,0,.54); }
.winner-lane { width:calc(100% - 40px); overflow:hidden; margin:27px 20px 0; }
.winner-lane-reverse { margin-top:9px; }
.winner-track { display:flex; width:max-content; align-items:center; gap:10px; padding-left:0; animation:live-winner-scroll 95s linear infinite; }
.winner-lane-reverse .winner-track { animation-direction:reverse; animation-duration:110s; }
.winner-chip { display:flex; min-width:328px; height:56px; align-items:center; gap:12px; box-sizing:border-box; padding:0 20px 0 8px; border-radius:30px; background:rgba(24,5,54,.78) url('../../static/images/livequiz/winner_player_bg.png') center/100% 100% no-repeat; color:#fff; font-size:19px; font-weight:700; white-space:nowrap; text-shadow:0 2px 3px rgba(0,0,0,.65); }
.winner-avatar { width:43px; height:43px; flex:none; border:1px solid rgba(255,243,188,.9); border-radius:50%; object-fit:cover; background:#5d286a; }
.live-list-empty { display:flex; min-height:340px; align-items:center; justify-content:center; box-sizing:border-box; padding:55px 60px; color:#fff; background:transparent url('../../static/images/livequiz/match_bg.png') center/100% 100% no-repeat; font-size:22px; font-weight:800; line-height:1.35; text-align:center; text-shadow:0 3px 6px rgba(0,0,0,.62); }
.live-list-more { display:block; min-width:175px; min-height:48px; margin:28px auto 0; border:1px solid #ff799e; border-radius:25px; color:#fff; background:#ad146c; font-size:17px; font-weight:800; }
@keyframes live-winner-scroll { from { transform:translateX(0); } to { transform:translateX(-50%); } }
</style>



// WEBPACK FOOTER //
// src/page/liveQuizList.vue